import { Add, PlayArrow, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useApprovalsController } from 'api/controllers/ApprovalsController';
import { useCompanyController } from 'api/controllers/CompanyController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import {
  ApprovalRule,
  Company,
  CounterpartyQueryFilters,
  CounterpartyType,
  CurrencyDropDown,
  InvoiceExpenseType
} from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { useModal } from 'hooks/useModal';

import { AppRoutesEnum } from 'utils/routes';

import {
  loadingContainerStyle,
  actionButtonsContainerStyle
} from 'styles/components/Common';
import {
  containerStyle,
  headerStyle,
  scrollableContainerStyle,
  helperBoxStyle,
  helperInfoIconStyle,
  helperTextStyle,
  rulesListContainerStyle,
  defaultRuleContainerStyle
} from 'styles/pages/ApprovalsStyle';

import { ApprovalRuleRow } from './components/ApprovalRuleRow';
import { DragContextProvider } from './context/DragContext';
import { ApprovalRuleSimulatorModal } from './simulator/ApprovalRuleSimulatorModal';

export const Approvals = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { translate } = useTranslations();
  const {
    getAllApprovalRulesForCompany,
    patchApprovalRule,
    deleteApprovalRule
  } = useApprovalsController();
  const { getExpenseTypes, getCurrencies } = useAdditionalFieldsController();
  const { getFilteredCounterpartiesByCompanyId } = useCounterPartyController();
  const { getCompanyDetailsById } = useCompanyController();

  const [rules, setRules] = useState<ApprovalRule[]>([]);
  const [defaultRule, setDefaultRule] = useState<ApprovalRule | null>(null);
  const [loading, setLoading] = useState(true);

  const {
    isOpen: isSimulatorOpen,
    openModal: openSimulator,
    closeModal: closeSimulator
  } = useModal();

  const [expenseTypes, setExpenseTypes] = useState<InvoiceExpenseType[]>();
  const [counterparties, setCounterparties] = useState<any[]>();
  const [currencies, setCurrencies] = useState<CurrencyDropDown[]>();
  const [company, setCompany] = useState<Company>();

  const fetchApprovalRules = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);

    try {
      const data = await getAllApprovalRulesForCompany(Number(companyId));
      setRules(data?.rules ?? []);
      setDefaultRule(data?.defaultRule ?? null);
    } catch (error) {
      console.error('Failed to fetch approval rules:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, getAllApprovalRulesForCompany]);

  const fetchExpenseTypes = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await getExpenseTypes(Number(companyId));
      setExpenseTypes(data);
    } catch (error) {
      console.error('Failed to fetch expense types:', error);
    }
  }, [companyId, getExpenseTypes]);

  const fetchCounterparties = useCallback(async () => {
    if (!companyId) return;
    try {
      const filters: CounterpartyQueryFilters = {
        types: [CounterpartyType.SUPPLIER]
      };
      const data = await getFilteredCounterpartiesByCompanyId(
        Number(companyId),
        filters
      );
      setCounterparties(data.counterparties);
    } catch (error) {
      console.error('Failed to fetch counterparties:', error);
    }
  }, [companyId, getFilteredCounterpartiesByCompanyId]);

  const fetchCurrencies = useCallback(async () => {
    try {
      const data = await getCurrencies();
      setCurrencies(data);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
    }
  }, [getCurrencies]);

  const fetchCompany = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await getCompanyDetailsById(Number(companyId));
      setCompany(data);
    } catch (error) {
      console.error('Failed to fetch company details:', error);
    }
  }, [companyId, getCompanyDetailsById]);

  useEffect(() => {
    fetchApprovalRules();
    fetchExpenseTypes();
    fetchCounterparties();
    fetchCurrencies();
    fetchCompany();
  }, [
    fetchApprovalRules,
    fetchExpenseTypes,
    fetchCounterparties,
    fetchCurrencies,
    fetchCompany
  ]);

  const handleTestRules = () => {
    openSimulator();
  };

  const handleNewRule = () => {
    if (!companyId) return;
    navigate(
      AppRoutesEnum.COMPANY_APPROVAL_RULES_NEW.replace(':companyId', companyId)
    );
  };

  const handleEditRule = (ruleId: number, isDefault = false) => {
    if (!companyId) return;
    navigate(
      AppRoutesEnum.COMPANY_APPROVAL_RULES_EDIT.replace(
        ':companyId',
        companyId
      ).replace(':ruleId', ruleId.toString()) +
        (isDefault ? '?fallback=true' : '')
    );
  };

  const handleDrop = useCallback(
    async (draggedId: number, targetId: number) => {
      if (!companyId) return;

      const targetIndex = rules.findIndex((r) => r.id === targetId);
      const newPriority = targetIndex + 1;

      try {
        await patchApprovalRule(Number(companyId), draggedId, {
          priority: newPriority
        });

        setRules((prevRules) => {
          const newRules = [...prevRules];
          const srcIndex = newRules.findIndex((r) => r.id === draggedId);
          const tgtIndex = newRules.findIndex((r) => r.id === targetId);

          const [draggedRule] = newRules.splice(srcIndex, 1);
          newRules.splice(tgtIndex, 0, draggedRule);

          return newRules.map((rule, index) => ({
            ...rule,
            priority: index + 1
          }));
        });
      } catch (error) {
        console.error('Failed to update approval rule priority:', error);
      }
    },
    [companyId, patchApprovalRule, rules]
  );

  const handleToggleActive = useCallback(
    async (ruleId: number, isActive: boolean) => {
      if (!companyId) return;

      try {
        await patchApprovalRule(Number(companyId), ruleId, {
          active: isActive
        });

        setRules((prev) =>
          prev.map((rule) =>
            rule.id === ruleId ? { ...rule, active: isActive } : rule
          )
        );
      } catch (error) {
        console.error('Failed to patch approval rule:', error);
      }
    },
    [companyId, patchApprovalRule]
  );

  const handleDelete = useCallback(
    async (ruleId: number) => {
      if (!companyId) return;

      try {
        await deleteApprovalRule(Number(companyId), ruleId);
        setRules((prev) => prev.filter((rule) => rule.id !== ruleId));
      } catch (error) {
        console.error('Failed to delete approval rule:', error);
      }
    },
    [companyId, deleteApprovalRule]
  );

  if (loading) {
    return (
      <Box sx={loadingContainerStyle}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={containerStyle}>
      <Box sx={headerStyle}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          {translate('approvals.approvalRules')}
        </Typography>
        <Box sx={actionButtonsContainerStyle}>
          <Button
            color="primary"
            startIcon={<PlayArrow />}
            onClick={handleTestRules}
            sx={{ fontSize: '12px', padding: '0.3125rem 0.625rem' }}
          >
            {translate('approvals.testRules')}
          </Button>
          <Button
            color="primary"
            startIcon={<Add />}
            onClick={handleNewRule}
            sx={{ fontSize: '12px', padding: '0.3125rem 0.625rem' }}
          >
            {translate('approvals.newRule')}
          </Button>
        </Box>
      </Box>

      <Box sx={scrollableContainerStyle}>
        <Box sx={helperBoxStyle}>
          <InfoOutlined sx={helperInfoIconStyle} />
          <Typography variant="body2" sx={helperTextStyle}>
            {translate('approvals.rulesHelperText')}
          </Typography>
        </Box>

        {rules.length > 0 && (
          <Paper elevation={3} sx={rulesListContainerStyle}>
            <DragContextProvider onDrop={handleDrop}>
              {rules.map((rule, index) => {
                const isFirst = index === 0;
                const isLast = index === rules.length - 1;
                return (
                  <ApprovalRuleRow
                    key={`rule-${rule.id}`}
                    rule={rule}
                    isFirst={isFirst}
                    isLast={isLast}
                    onToggleActive={handleToggleActive}
                    onDelete={handleDelete}
                    onClick={() => rule.id && handleEditRule(rule.id)}
                  />
                );
              })}
            </DragContextProvider>
          </Paper>
        )}

        {defaultRule && (
          <Paper elevation={3} sx={defaultRuleContainerStyle}>
            <ApprovalRuleRow
              key={`default-${defaultRule.id}`}
              rule={defaultRule}
              isDefault={true}
              onDelete={() => {}}
              onClick={() =>
                defaultRule.id && handleEditRule(defaultRule.id, true)
              }
            />
          </Paper>
        )}
      </Box>
      {companyId && (
        <ApprovalRuleSimulatorModal
          isOpen={isSimulatorOpen}
          onClose={closeSimulator}
          companyId={Number(companyId)}
          expenseTypes={expenseTypes}
          counterparties={counterparties}
          currencies={currencies}
          defaultCurrencyId={company?.currencyId}
          activeRulesCount={rules.filter((r) => r.active).length}
        />
      )}
    </Box>
  );
};
